import {JackingFormInstanceEditor} from '../../models/jacking-v2/jacking.models';
import {deepClone} from './dirty-tracking.service';

// Builds a stable snapshot for dirty-checking by removing template/computed/options and UI-seeded placeholders.
export function buildJackingDirtySnapshot(editor: JackingFormInstanceEditor): any {
  if (!editor) {
    return null;
  }

  const snap: any = deepClone(editor);
  const initializationOnlyFieldIds = getInitializationOnlyFieldIds(snap.template);

  // never include template / computed / options in dirty checks
  delete snap.revision;
  delete snap.statusId;
  delete snap.template;
  delete snap.optionsByKey;
  delete snap.dataByKey;
  // valuesByFieldId is the representation bound by the V2 controls. fieldValues is the
  // API's duplicate representation and comparing both can report the same input twice.
  delete snap.fieldValues;

  // missing obj stuff
  delete snap.customMissingProviders;
  delete snap._missingProvidersRegistered;

  stripUiOnlyFromEvents(snap);
  stripEmptyLegSummary(snap);
  stripEmptyIdZeroShells(snap);
  stripEmptyLiftPumpsUsed(snap);
  stripEmptyValuesByFieldId(snap, initializationOnlyFieldIds);

  return snap;
}

// Removes UI-only fields and empty placeholder legs injected by the events table so viewing it doesn't mark dirty.
function stripUiOnlyFromEvents(snap: any) {
  const rows = snap?.jackingEvents?.eventRows;
  if (!Array.isArray(rows)) { return; }

  rows.forEach((r: any) => {
    delete r.timeToShow;
    delete r.selectedLocalDateIso;
    // Event components add transient state after server data has loaded. None of these
    // underscore-prefixed properties represent values that should be saved.
    Object.keys(r).forEach(k => {
      if (k.startsWith('_')) {
        delete r[k];
      }
    });

    if (r.eventDate) {
      r.eventDate = normalizePtLuxonDate(r.eventDate);
    }

    if (Array.isArray(r.legs)) {
      r.legs = r.legs.filter((l: any) => !isEmptyIdZeroLeg(l));
      if (r.legs.length === 0) { delete r.legs; }
    }
  });
}


// Returns true when a leg is an id=0 placeholder with no meaningful user-entered values.
function isEmptyIdZeroLeg(l: any): boolean {
  if (!l || l.id !== 0) {
    return false;
  }

  const keys = Object.keys(l).filter(k => k !== 'id' && k !== 'legNumber');
  return keys.every(k => l[k] === null || l[k] === undefined || l[k] === '' || l[k] === false);
}

// Removes id=0 "shell" objects seeded by UI components (e.g. final values) when they contain no user-entered data.
function stripEmptyIdZeroShells(snap: any) {
  const root = snap?.jackingFinalValues;
  if (!root || typeof root !== 'object') {
    return;
  }

  Object.keys(root).forEach(k => {
    const v = root[k];
    if (isEmptyIdZeroObject(v)) {
      delete root[k];
    }
  });

  if (Object.keys(root).length === 0) {
    delete snap.jackingFinalValues;
  }
}

// Removes UI-seeded map entries that don't contain a real user-entered value.
function stripEmptyValuesByFieldId(snap: any, initializationOnlyFieldIds: Set<string>) {
  const map = snap?.valuesByFieldId;
  if (!map || typeof map !== 'object') {
    return;
  }

  Object.keys(map).forEach(k => {
    const v = map[k];
    if (initializationOnlyFieldIds.has(String(k)) || isEmptyFieldValue(v)) {
      delete map[k];
    }
  });

  if (Object.keys(map).length === 0) {
    delete snap.valuesByFieldId;
  }
}

function getInitializationOnlyFieldIds(template: any): Set<string> {
  const ids = new Set<string>();
  (template?.sections || []).forEach((section: any) => {
    (section?.groups || []).forEach((group: any) => {
      (group?.fields || []).forEach((field: any) => {
        if (field?.readOnly && field.id !== null && field.id !== undefined) {
          ids.add(String(field.id));
        }
      });
    });
  });
  return ids;
}

// Opening the leg-summary tab creates blank rows. They remain placeholders after the API assigns IDs.
function stripEmptyLegSummary(snap: any) {
  const summary = snap?.jackingLegSummary;
  if (summary === null || summary === undefined) {
    delete snap.jackingLegSummary;
    return;
  }
  if (typeof summary !== 'object') {
    return;
  }

  const legs = Array.isArray(summary.legs) ? summary.legs : [];
  const hasEnteredLeg = legs.some((leg: any) => !isEmptyLeg(leg, true));
  const hasEnteredRootValue = Object.keys(summary)
    .filter(key => key !== 'id' && key !== 'legs')
    .some(key => !isDefaultPlaceholderValue(summary[key]));

  if (!hasEnteredLeg && !hasEnteredRootValue) {
    delete snap.jackingLegSummary;
  }
}

function isDefaultPlaceholderValue(value: any): boolean {
  return value === null || value === undefined || value === '' || value === false;
}

function isEmptyLeg(leg: any, allowPersistedId = false): boolean {
  if (!leg || typeof leg !== 'object' || leg.id === null || leg.id === undefined ||
      (leg.id !== 0 && !allowPersistedId)) {
    return false;
  }

  return Object.keys(leg)
    .filter(key => key !== 'id' && key !== 'legNumber')
    .every(key => isDefaultPlaceholderValue(leg[key]));
}

function isEmptyFieldValue(v: any): boolean {
  if (!v || typeof v !== 'object') {
    return true;
  }

  // Only fieldTemplateId = seeded placeholder
  const keys = Object.keys(v);
  if (keys.length === 1 && keys[0] === 'fieldTemplateId') {
    return true;
  }

  // If your value model uses these fields, treat absence as empty
  const hasValue =
    v.stringValue != null && v.stringValue !== '' ||
    v.intValue != null ||
    v.decimalValue != null ||
    v.boolValue === true || // only true counts as user input here
    v.dateTimeValue != null ||
    v.selectedOptionId != null ||
    v.value != null && v.value !== '';

  return !hasValue;
}

// Returns true when an id=0 shell has only empty placeholder legs and default fields, so it can be ignored for dirty checks.
function isEmptyIdZeroObject(v: any): boolean {
  if (!v || typeof v !== 'object' || v.id !== 0) {
    return false;
  }

  if (Array.isArray(v.legs)) {
    v.legs = v.legs.filter((l: any) => !isEmptyIdZeroLeg(l));
    if (v.legs.length > 0) {
      return false;
    }
  }

  const ignore = new Set(['id', 'legs', 'timeUtc', 'timeOffset', 'timeConfirmed']);
  const keys = Object.keys(v).filter(k => !ignore.has(k));

  return keys.every(k => v[k] === null || v[k] === undefined || v[k] === '' || v[k] === false);
}

function normalizePtLuxonDate(x: any): any {
  if (!x || typeof x !== 'object') {
    return x;
  }

  // Keep only the fields that represent actual user data
  // dateTime may be a Luxon DateTime or null
  const dt = (x as any).dateTime ?? null;

  // If you also store offset separately elsewhere, you can ignore it here.
  // If you rely on it inside the Pt object, keep it.
  const offset = (x as any).offset ?? (x as any).timeOffset ?? null;

  // Return a stable minimal shape
  const res: any = { dateTime: dt };
  if (offset !== null && offset !== undefined) {
    res.offset = offset;
  }
  return res;
}

function stripEmptyLiftPumpsUsed(snap: any) {
  const lp = snap?.jackingFinalValues?.liftPumpsUsed;
  if (!lp || typeof lp !== 'object') {
    return;
  }

  // Remove placeholder legs: id=0 and liftPumpUsed=false only
  if (Array.isArray(lp.legs)) {
    lp.legs = lp.legs.filter((l: any) => !isEmptyLiftPumpLeg(l));
    if (lp.legs.length === 0) {
      delete lp.legs;
    }
  }

  // If nothing meaningful remains, remove liftPumpsUsed entirely
  const hasMeaningfulDepth = lp.depthDeployed === true; // only true counts as user input
  const hasLegs = Array.isArray(lp.legs) && lp.legs.length > 0;

  if (!hasMeaningfulDepth && !hasLegs) {
    delete snap.jackingFinalValues.liftPumpsUsed;
  }

  // If jackingFinalValues becomes empty after this, optionally clean it up too
  if (
    snap.jackingFinalValues &&
    typeof snap.jackingFinalValues === 'object' &&
    Object.keys(snap.jackingFinalValues).length === 0
  ) {
    delete snap.jackingFinalValues;
  }
}

function isEmptyLiftPumpLeg(l: any): boolean {
  if (!l || l.id !== 0) {
    return false;
  }

  // Treat only default false as empty. If true, it's user-entered.
  const hasUserChoice = l.liftPumpUsed === true;
  return !hasUserChoice;
}



// WEBPACK FOOTER //
// ./src/services/jacking-v2/jacking-form-dirty-snapshot.ts